import { FC, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { TaskItem, TeamMember, TodoTasksBoard } from '@demind-inc/core';
import {
  AddRounded,
  CheckRounded,
  ExpandMoreRounded,
  FilterListRounded,
  ViewKanbanRounded,
} from '@mui/icons-material';
import { CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import { useLocalStorage } from '@uidotdev/usehooks';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';

import './TeamTasksView.scss';
import TaskDetailModal, { TeamTaskModalSaveParams } from '../../TaskDetailModal/TaskDetailModal';
import {
  selectedDateAtom,
  useCreateTeamBoard,
  useCreateTeamTask,
  useDeleteTeamTask,
  useTaskActions,
  useTeamBoardTasks,
  useSharedTeamTasks,
  useUpdateTeamTask,
  useUpdateTeamTaskAssignment,
  useUpdateTeamTaskCompleteStatus,
  visibleTaskDetailAtom,
} from '../../../data-access';
import { getDueDateYYYYMMDD } from '../../../helpers';
import type { ProjectFilterItem } from '../../../hooks';
import { AddProjectDialog, ProjectSelector } from '../../Project';
import { formatDataDate } from '../../DataView/i18n';
import TeamTasksKanbanView from '../TeamTasksKanbanView';
import { getTeamTasksFilterLabel, getTeamTasksGroupLabel, getTeamTasksLayoutLabel } from '../i18n';
import {
  TeamTaskGroup,
  TeamTaskWithOwner,
  TeamTasksFilter,
  TeamTasksGroupBy,
  buildTeamTaskGroups,
  filterTeamTasks,
  getTeamMembersNotSharingTasks,
  getTeamTaskAssigneeUserId,
  getTeamTaskForDisplay,
  isTeamBoardTask,
  mapTeamTasksForDisplay,
} from '../utils';
import {
  ALL_PROJECT_FILTER,
  BOARD_FILTER_ALL,
  BUCKET_CLASS_NAMES,
  GROUP_OPTIONS,
  GROUP_SHORTCUTS,
  LAYOUT_OPTIONS,
  SHARED_FILTER_OPTIONS,
  TEAM_FILTER_OPTIONS,
  TEAM_TASKS_BOARD_FILTER_STORAGE_KEY,
  TEAM_TASKS_GROUP_BY_STORAGE_KEY,
  TEAM_TASKS_LAYOUT_STORAGE_KEY,
  TEAM_TASKS_SEGMENT_STORAGE_KEY,
} from './constants';
import { isTeamTasksGroupBy, isTeamTasksLayout, isTeamTasksSegment } from './helpers';
import TeamTaskGroupSection from './TeamTaskGroupSection';
import TeamTasksHiddenMembersFooter from './TeamTasksHiddenMembersFooter';
import type { TeamTasksLayout, TeamTasksSegment } from './types';

interface TeamTasksViewProps {
  teamId: string;
  members: TeamMember[];
  boards: TodoTasksBoard[];
  currentUserId: string;
}

const TeamTasksView: FC<TeamTasksViewProps> = ({ teamId, members, boards, currentUserId }) => {
  const { t, i18n } = useTranslation();
  const [storedSegment, setStoredSegment] = useLocalStorage<TeamTasksSegment>(
    TEAM_TASKS_SEGMENT_STORAGE_KEY,
    'team'
  );
  const [teamFilter, setTeamFilter] = useState<TeamTasksFilter>('all');
  const [sharedFilter, setSharedFilter] = useState<TeamTasksFilter>('all');
  const [storedBoardFilter, setStoredBoardFilter] = useLocalStorage<string>(
    TEAM_TASKS_BOARD_FILTER_STORAGE_KEY,
    BOARD_FILTER_ALL
  );
  const [isTaskModalReadOnly, setIsTaskModalReadOnly] = useState(false);
  const [taskModalBoardName, setTaskModalBoardName] = useState('');
  const [teamTaskModalTask, setTeamTaskModalTask] = useState<TaskItem | null>(null);
  const [isCreatingTeamTask, setIsCreatingTeamTask] = useState(false);
  const [teamTaskModalAssigneeUserId, setTeamTaskModalAssigneeUserId] = useState('');
  const [isCreateBoardDialogOpen, setIsCreateBoardDialogOpen] = useState(false);
  const [storedGroupBy, setStoredGroupBy] = useLocalStorage<TeamTasksGroupBy>(
    TEAM_TASKS_GROUP_BY_STORAGE_KEY,
    'due'
  );
  const [storedLayout, setStoredLayout] = useLocalStorage<TeamTasksLayout>(
    TEAM_TASKS_LAYOUT_STORAGE_KEY,
    'list'
  );
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [groupMenuAnchorEl, setGroupMenuAnchorEl] = useState<HTMLElement | null>(null);
  const segment = isTeamTasksSegment(storedSegment) ? storedSegment : 'team';
  const groupBy = isTeamTasksGroupBy(storedGroupBy) ? storedGroupBy : 'due';
  const layout = isTeamTasksLayout(storedLayout) ? storedLayout : 'list';
  const selectedDate = useRecoilValue(selectedDateAtom);
  const visibleTask = useRecoilValue(visibleTaskDetailAtom);
  const setVisibleTask = useSetRecoilState(visibleTaskDetailAtom);
  const { updateTodoTaskStatus, isTaskStatusUpdating } = useTaskActions();
  const { createTeamBoard, isCreatingTeamBoard } = useCreateTeamBoard();
  const { createTeamTask, isCreatingTeamTask: isCreatingTeamTaskRequest } = useCreateTeamTask();
  const { updateTeamTask, isUpdatingTeamTask } = useUpdateTeamTask();
  const { updateTeamTaskAssignment, isUpdatingTeamTaskAssignment } = useUpdateTeamTaskAssignment();
  const { updateTeamTaskCompleteStatus, isUpdatingTeamTaskCompleteStatus } =
    useUpdateTeamTaskCompleteStatus();
  const { deleteTeamTask, isDeletingTeamTask } = useDeleteTeamTask();
  const selectedDateString = selectedDate.format('YYYY-MM-DD');
  const sharedTasksQuery = useSharedTeamTasks({
    userId: currentUserId,
    teamId: segment === 'shared' ? teamId : undefined,
    date: selectedDateString,
  });
  const teamBoardTasksQuery = useTeamBoardTasks({
    userId: currentUserId,
    teamId: segment === 'team' ? teamId : undefined,
    date: selectedDateString,
  });

  const activeBoards = useMemo(() => boards.filter((board) => !!board.todoTaskId), [boards]);
  const allProjectFilter = useMemo(
    () => ({
      ...ALL_PROJECT_FILTER,
      name: t('team.tasks.allProjects'),
    }),
    [t]
  );
  const projectFilterOptions = useMemo<ProjectFilterItem[]>(() => {
    return [
      allProjectFilter,
      ...activeBoards.map((board) => ({
        todoTaskId: board.todoTaskId,
        name: board.name,
        appFrom: board.appFrom,
      })),
    ];
  }, [activeBoards, allProjectFilter]);
  const currentMember = useMemo(
    () => members.find((member) => member.userId === currentUserId),
    [currentUserId, members]
  );
  const canManageTeamBoards = currentMember?.role === 'owner' || currentMember?.role === 'admin';
  const boardFilter = useMemo(() => {
    if (storedBoardFilter === BOARD_FILTER_ALL) {
      return BOARD_FILTER_ALL;
    }

    return activeBoards.some((board) => board.todoTaskId === storedBoardFilter)
      ? storedBoardFilter
      : BOARD_FILTER_ALL;
  }, [activeBoards, storedBoardFilter]);
  const selectedProjectFilter = useMemo(() => {
    return (
      projectFilterOptions.find((project) => project.todoTaskId === boardFilter) || allProjectFilter
    );
  }, [allProjectFilter, boardFilter, projectFilterOptions]);

  useEffect(() => {
    if (segment === 'shared' && teamFilter === 'mine') {
      setTeamFilter('all');
    }
  }, [segment, teamFilter]);

  useEffect(() => {
    if (segment === 'team' && groupBy === 'board' && boardFilter !== BOARD_FILTER_ALL) {
      setStoredGroupBy('due');
    }
  }, [boardFilter, groupBy, segment, setStoredGroupBy]);

  const teamBoardTasks = useMemo(
    () => mapTeamTasksForDisplay(teamBoardTasksQuery.tasks, currentUserId, t),
    [currentUserId, t, teamBoardTasksQuery.tasks]
  );
  const sharedTasks = useMemo(
    () => mapTeamTasksForDisplay(sharedTasksQuery.tasks, currentUserId, t),
    [currentUserId, sharedTasksQuery.tasks, t]
  );
  const activeSourceTasks = segment === 'team' ? teamBoardTasks : sharedTasks;
  const boardFilteredTeamTasks = useMemo(() => {
    if (segment !== 'team' || boardFilter === BOARD_FILTER_ALL) {
      return activeSourceTasks;
    }

    return activeSourceTasks.filter((taskItem) => taskItem.project.boardId === boardFilter);
  }, [activeSourceTasks, boardFilter, segment]);
  const activeFilter = segment === 'team' ? teamFilter : sharedFilter;
  const filterOptions = segment === 'team' ? TEAM_FILTER_OPTIONS : SHARED_FILTER_OPTIONS;
  const availableGroupOptions = useMemo(
    () =>
      GROUP_OPTIONS.filter(
        (option) =>
          option.value !== 'board' || segment !== 'team' || boardFilter === BOARD_FILTER_ALL
      ),
    [boardFilter, segment]
  );
  const selectedGroupOption =
    availableGroupOptions.find((option) => option.value === groupBy) || availableGroupOptions[0];
  const filteredTasks = useMemo(
    () => filterTeamTasks(boardFilteredTeamTasks, activeFilter),
    [activeFilter, boardFilteredTeamTasks]
  );
  const groups = useMemo(
    () => buildTeamTaskGroups(filteredTasks, groupBy, t),
    [filteredTasks, groupBy, t]
  );
  const hiddenMembers = useMemo(
    () => getTeamMembersNotSharingTasks(members, currentUserId, t),
    [currentUserId, members, t]
  );
  const activeFilterOption =
    filterOptions.find((option) => option.value === activeFilter) || filterOptions[0];

  const dateLabel = useMemo(
    () =>
      formatDataDate(i18n.language, selectedDate.toDate(), {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }),
    [i18n.language, selectedDate]
  );
  const activeQuery = segment === 'team' ? teamBoardTasksQuery : sharedTasksQuery;
  const isSavingTeamTask = isCreatingTeamTaskRequest || isUpdatingTeamTask;
  const isCompletingTask = isTaskStatusUpdating || isUpdatingTeamTaskCompleteStatus;
  const errorMessage = activeQuery.error instanceof Error ? activeQuery.error.message : null;

  const handleSegmentChange = (nextSegment: TeamTasksSegment) => {
    setStoredSegment(nextSegment);
  };

  const handleFilterChange = useCallback(
    (nextFilter: TeamTasksFilter) => {
      if (segment === 'team') {
        setTeamFilter(nextFilter);
        return;
      }

      setSharedFilter(nextFilter === 'mine' ? 'all' : nextFilter);
    },
    [segment]
  );

  const handleOpenFilterMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setFilterMenuAnchorEl(event.currentTarget);
  };

  const handleCloseFilterMenu = () => {
    setFilterMenuAnchorEl(null);
  };

  const handleFilterMenuChange = (nextFilter: TeamTasksFilter) => {
    handleFilterChange(nextFilter);
    handleCloseFilterMenu();
  };

  const handleOpenGroupMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setGroupMenuAnchorEl(event.currentTarget);
  };

  const handleCloseGroupMenu = () => {
    setGroupMenuAnchorEl(null);
  };

  const handleGroupByChange = (nextGroupBy: TeamTasksGroupBy) => {
    setStoredGroupBy(nextGroupBy);
    handleCloseGroupMenu();
  };

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (visibleTask || teamTaskModalTask || isCreateBoardDialogOpen) {
        return;
      }

      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }

      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
        return;
      }

      const filterIndex = Number(event.key) - 1;
      if (Number.isInteger(filterIndex) && filterIndex >= 0 && filterIndex < filterOptions.length) {
        event.preventDefault();
        handleFilterChange(filterOptions[filterIndex].value);
        return;
      }

      const nextGroup = availableGroupOptions.find(
        (option) => GROUP_SHORTCUTS[option.value].toLowerCase() === event.key.toLowerCase()
      );

      if (nextGroup) {
        event.preventDefault();
        setStoredGroupBy(nextGroup.value);
      }
    };

    window.addEventListener('keydown', handleShortcut);

    return () => {
      window.removeEventListener('keydown', handleShortcut);
    };
  }, [
    availableGroupOptions,
    filterOptions,
    handleFilterChange,
    isCreateBoardDialogOpen,
    setStoredGroupBy,
    teamTaskModalTask,
    visibleTask,
  ]);

  const handleCreateTeamBoard = async (name: string) => {
    await createTeamBoard({
      teamId,
      teamBoardCreateReq: {
        name,
      },
    });
  };

  const buildTeamTaskPayload = ({ taskInfo, assigneeUserId }: TeamTaskModalSaveParams) => {
    return {
      name: taskInfo.name,
      desc: taskInfo.desc || '',
      dueDateTime: taskInfo.dueDateTime,
      duration: taskInfo.duration,
      priority: taskInfo.priority,
      assigneeUserId: assigneeUserId || null,
    };
  };

  const handleSaveTeamTask = async (params: TeamTaskModalSaveParams) => {
    const body = buildTeamTaskPayload(params);

    if (params.taskId) {
      await updateTeamTask({
        teamId,
        boardId: params.boardId,
        taskId: params.taskId,
        body: {
          name: body.name,
          desc: body.desc,
          dueDateTime: body.dueDateTime,
          duration: body.duration,
          priority: body.priority,
        },
      });
      return;
    }

    const createdTask = await createTeamTask({
      teamId,
      boardId: params.boardId,
      body,
    });
  };

  const handleOpenCreateTeamTask = (defaults?: { boardId?: string; assigneeUserId?: string }) => {
    const defaultBoard =
      activeBoards.find((board) => board.todoTaskId === defaults?.boardId) ||
      activeBoards.find((board) => board.todoTaskId === boardFilter) ||
      activeBoards[0];

    if (!defaultBoard) {
      return;
    }

    setIsCreatingTeamTask(true);
    setTeamTaskModalAssigneeUserId(defaults?.assigneeUserId || '');
    setTeamTaskModalTask({
      name: '',
      desc: '',
      boardId: defaultBoard.todoTaskId,
      appFrom: defaultBoard.appFrom || 'lifestack',
      dueDateTime: { date: selectedDateString, timezone: dayjs.tz.guess() },
      duration: { amount: 30, unit: 'minute' },
      labels: [],
      subTasks: [],
      completed: false,
      rawJson: {},
    } as TaskItem);
  };

  const handleCreateInGroup = (group: TeamTaskGroup, activeGroupBy: TeamTasksGroupBy) => {
    handleOpenCreateTeamTask({
      boardId: activeGroupBy === 'board' ? group.key : undefined,
      assigneeUserId: activeGroupBy === 'member' ? group.owner?.id : undefined,
    });
  };

  const handleOpenTeamTask = (taskItem: TeamTaskWithOwner) => {
    setIsCreatingTeamTask(false);
    setTeamTaskModalAssigneeUserId(getTeamTaskAssigneeUserId(taskItem));
    setTeamTaskModalTask(getTeamTaskForDisplay(taskItem, t));
  };

  const handleOpenSharedTask = (taskItem: TeamTaskWithOwner) => {
    setIsTaskModalReadOnly(!taskItem.permissions.canEdit);
    setTaskModalBoardName(taskItem.project.name || '');
    setVisibleTask(getTeamTaskForDisplay(taskItem, t));
  };

  const handleOpenTask = (taskItem: TeamTaskWithOwner) => {
    if (isTeamBoardTask(taskItem)) {
      handleOpenTeamTask(taskItem);
      return;
    }

    handleOpenSharedTask(taskItem);
  };

  const handleAssignTeamTask = async (taskItem: TeamTaskWithOwner, assigneeUserId: string) => {
    const task = getTeamTaskForDisplay(taskItem, t);
    if (!task.taskId) {
      return;
    }

    await updateTeamTaskAssignment({
      teamId,
      boardId: task.boardId || taskItem.project.boardId,
      taskId: task.taskId,
      teamTaskAssignmentReq: { assigneeUserId: assigneeUserId || null },
    });
  };

  const handleToggleTask = async (taskItem: TeamTaskWithOwner, completed: boolean) => {
    const task = getTeamTaskForDisplay(taskItem, t);
    if (!task.taskId) {
      return;
    }

    if (isTeamBoardTask(taskItem)) {
      if (!taskItem.permissions.canComplete) {
        return;
      }

      await updateTeamTaskCompleteStatus({
        teamId,
        boardId: task.boardId || taskItem.project.boardId,
        taskId: task.taskId,
        completed,
      });
      return;
    }

    if (!taskItem.permissions.canComplete) {
      return;
    }

    await updateTodoTaskStatus({
      taskId: task.taskId,
      boardId: task.boardId || taskItem.project.boardId,
      completed,
      originalTaskId: task.originalTaskId,
      mutateParams: {
        date: getDueDateYYYYMMDD(task.dueDateTime) || selectedDateString,
      },
    });
  };

  const handleCloseTeamTaskModal = () => {
    setTeamTaskModalTask(null);
    setIsCreatingTeamTask(false);
    setTeamTaskModalAssigneeUserId('');
  };

  const renderHiddenMembersFooter = (isKanbanInset = false) => {
    return (
      <TeamTasksHiddenMembersFooter
        segment={segment}
        hiddenMembers={hiddenMembers}
        isKanbanInset={isKanbanInset}
      />
    );
  };

  const renderEmptyCopy = () => {
    if (segment === 'team' && activeFilter === 'mine') {
      return t('team.tasks.emptyAssignedToYou');
    }

    return t('team.tasks.emptyFilter');
  };

  const renderNoTeamBoardsCard = () => {
    return (
      <div className="team-tasks-view__body team-tasks-view__body--centered">
        <div className="team-tasks-view__empty-state team-tasks-view__empty-state--board">
          <span className="team-tasks-view__empty-icon" aria-hidden="true">
            <ViewKanbanRounded />
          </span>
          <Typography variant="body2" component="p" className="team-tasks-view__state-title">
            {t('team.tasks.createProjectTitle')}
          </Typography>
          <Typography variant="subtitle2" component="p" className="team-tasks-view__state-copy">
            {t('team.tasks.createProjectDescription')}
          </Typography>
          {canManageTeamBoards ? (
            <div className="team-tasks-view__empty-actions">
              <button
                type="button"
                className="team-page__button team-page__button--primary team-tasks-view__empty-action"
                onClick={() => setIsCreateBoardDialogOpen(true)}
              >
                <AddRounded className="team-tasks-view__new-task-icon" />
                <Typography variant="subtitle2" component="span">
                  {t('team.projects.addProject')}
                </Typography>
              </button>
            </div>
          ) : (
            <Typography
              variant="subtitle2"
              component="p"
              className="team-tasks-view__state-copy team-tasks-view__state-copy--muted"
            >
              {t('team.tasks.askOwnerCreateProject')}
            </Typography>
          )}
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (segment === 'team' && !activeBoards.length) {
      return renderNoTeamBoardsCard();
    }

    if (activeQuery.isLoading && !activeQuery.teamTasks) {
      return (
        <div className="team-tasks-view__state" aria-live="polite">
          <CircularProgress size={26} className="team-tasks-view__spinner" />
          <Typography variant="body2" component="p" className="team-tasks-view__state-title">
            {t('team.tasks.loading')}
          </Typography>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="team-tasks-view__state" role="alert">
          <Typography variant="body2" component="p" className="team-tasks-view__state-title">
            {t('team.tasks.loadError')}
          </Typography>
          <Typography variant="subtitle2" component="p" className="team-tasks-view__state-copy">
            {errorMessage}
          </Typography>
        </div>
      );
    }

    if (!groups.length) {
      return (
        <div className="team-tasks-view__body">
          <div className="team-tasks-view__empty-state">
            <span className="team-tasks-view__empty-icon" aria-hidden="true">
              <CheckRounded />
            </span>
            <Typography variant="body2" component="p" className="team-tasks-view__state-title">
              {t('team.tasks.nothingHere')}
            </Typography>
            <Typography variant="subtitle2" component="p" className="team-tasks-view__state-copy">
              {renderEmptyCopy()}
            </Typography>
          </div>
          {renderHiddenMembersFooter()}
        </div>
      );
    }

    return (
      <div
        className={clsx(
          'team-tasks-view__body',
          layout === 'kanban' && 'team-tasks-view__body--kanban'
        )}
      >
        <div className="team-tasks-view__groups">
          {layout === 'kanban' ? (
            <TeamTasksKanbanView
              tasks={filteredTasks}
              segment={segment}
              groupBy={groupBy}
              members={members}
              currentUserId={currentUserId}
              isTaskStatusUpdating={isCompletingTask}
              isAssigningTask={isUpdatingTeamTaskAssignment}
              onOpenTask={handleOpenTask}
              onToggleTask={handleToggleTask}
              onAssignTask={handleAssignTeamTask}
              onCreateInGroup={handleCreateInGroup}
            />
          ) : (
            groups.map((group) => (
              <TeamTaskGroupSection
                key={group.key}
                group={group}
                segment={segment}
                groupBy={groupBy}
                members={members}
                currentUserId={currentUserId}
                isTaskStatusUpdating={isCompletingTask}
                isAssigningTask={isUpdatingTeamTaskAssignment}
                onOpenTask={handleOpenTask}
                onToggleTask={handleToggleTask}
                onAssignTask={handleAssignTeamTask}
                onCreateInGroup={handleCreateInGroup}
              />
            ))
          )}
          {renderHiddenMembersFooter(layout === 'kanban')}
        </div>
      </div>
    );
  };

  const segmentDescription =
    segment === 'team'
      ? t('team.tasks.teamSegmentDescription')
      : t('team.tasks.sharedSegmentDescription');

  return (
    <>
      <main className="team-tasks-view">
        <header className="team-tasks-view__header">
          <div className="team-tasks-view__header-row">
            <div className="team-tasks-view__header-copy">
              <Typography variant="body2" component="p" className="team-tasks-view__date-label">
                {dateLabel}
              </Typography>
              <div className="team-tasks-view__title-row">
                <Typography variant="h2" component="h1" className="team-tasks-view__title">
                  {t('team.tasks.title')}
                </Typography>
                <div
                  className="team-tasks-view__segment-control team-tasks-view__segment-control--header"
                  aria-label={t('team.tasks.sourceAria')}
                >
                  <button
                    type="button"
                    className={clsx(
                      'team-tasks-view__segment-button',
                      segment === 'team' && 'team-tasks-view__segment-button--active'
                    )}
                    onClick={() => handleSegmentChange('team')}
                  >
                    {t('team.tasks.teamTasks')}
                  </button>
                  <button
                    type="button"
                    className={clsx(
                      'team-tasks-view__segment-button',
                      segment === 'shared' && 'team-tasks-view__segment-button--active'
                    )}
                    onClick={() => handleSegmentChange('shared')}
                  >
                    {t('team.tasks.sharedTasks')}
                  </button>
                </div>
              </div>
              <Typography
                variant="body2"
                component="p"
                className="team-tasks-view__segment-description"
              >
                {segmentDescription}
              </Typography>
            </div>
            <div className="team-tasks-view__header-actions">
              {activeQuery.isFetching && (
                <div
                  className="team-tasks-view__updating-block"
                  aria-label={t('team.tasks.updatingAria')}
                  aria-live="polite"
                >
                  <CircularProgress size={16} className="team-tasks-view__updating-spinner" />
                  <Typography
                    variant="subtitle2"
                    component="span"
                    className="team-tasks-view__updating-label"
                  >
                    {t('team.common.updating')}
                  </Typography>
                </div>
              )}
              {segment === 'team' && (
                <button
                  type="button"
                  className="team-page__button team-page__button--primary team-tasks-view__new-task-button"
                  disabled={!activeBoards.length}
                  onClick={() => handleOpenCreateTeamTask()}
                >
                  <AddRounded className="team-tasks-view__new-task-icon" />
                  <Typography variant="subtitle2" component="span">
                    {t('team.tasks.newTask')}
                  </Typography>
                </button>
              )}
            </div>
          </div>

          <div className="team-tasks-view__toolbar">
            <div className="team-tasks-view__toolbar-main">
              {segment === 'team' && activeBoards.length > 0 && (
                <ProjectSelector
                  projects={projectFilterOptions}
                  selectedProject={selectedProjectFilter}
                  onProjectSelect={setStoredBoardFilter}
                  onProjectAdd={
                    canManageTeamBoards ? () => setIsCreateBoardDialogOpen(true) : undefined
                  }
                  enableToAdd={canManageTeamBoards}
                  className="team-tasks-view__project-selector"
                  menuClassName="team-tasks-view__project-selector-menu"
                  disableSelectedProjectTooltip
                  showChevron
                />
              )}

              <button
                type="button"
                className="team-tasks-view__toolbar-button team-tasks-view__filter-select"
                aria-label={t('team.tasks.filterAria')}
                aria-haspopup="menu"
                aria-expanded={Boolean(filterMenuAnchorEl)}
                onClick={handleOpenFilterMenu}
              >
                <FilterListRounded className="team-tasks-view__toolbar-button-icon" />
                <Typography variant="subtitle2" component="span">
                  {t('team.tasks.filter')}
                </Typography>
                <span className="team-tasks-view__toolbar-button-separator" aria-hidden="true">
                  ·
                </span>
                {activeFilterOption?.bucket && (
                  <span
                    className={clsx(
                      'team-tasks-view__bucket-dot',
                      BUCKET_CLASS_NAMES[activeFilterOption.bucket]
                    )}
                    aria-hidden="true"
                  />
                )}
                <Typography
                  variant="subtitle2"
                  component="span"
                  className="team-tasks-view__filter-select-summary"
                >
                  {activeFilterOption
                    ? getTeamTasksFilterLabel(t, activeFilterOption.value)
                    : t('team.tasks.filters.all')}
                </Typography>
                <ExpandMoreRounded className="team-tasks-view__group-select-icon" />
              </button>
              <Menu
                anchorEl={filterMenuAnchorEl}
                open={Boolean(filterMenuAnchorEl)}
                onClose={handleCloseFilterMenu}
                className="team-tasks-view__filter-menu"
              >
                {filterOptions.map((option, index) => {
                  const isSelected = activeFilter === option.value;

                  return (
                    <MenuItem
                      key={option.value}
                      className={clsx(
                        'team-tasks-view__filter-menu-item',
                        isSelected && 'team-tasks-view__filter-menu-item--selected'
                      )}
                      onClick={() => handleFilterMenuChange(option.value)}
                    >
                      <span className="team-tasks-view__shortcut-chip">{index + 1}</span>
                      <span className="team-tasks-view__filter-menu-option">
                        {option.bucket && (
                          <span
                            className={clsx(
                              'team-tasks-view__bucket-dot',
                              BUCKET_CLASS_NAMES[option.bucket]
                            )}
                            aria-hidden="true"
                          />
                        )}
                        <Typography variant="body2" component="span">
                          {getTeamTasksFilterLabel(t, option.value)}
                        </Typography>
                      </span>
                      {isSelected && <CheckRounded className="team-tasks-view__menu-check" />}
                    </MenuItem>
                  );
                })}
              </Menu>
            </div>

            <div className="team-tasks-view__view-control">
              <button
                type="button"
                className="team-tasks-view__toolbar-button team-tasks-view__group-select"
                aria-label={t('team.tasks.groupAria')}
                aria-haspopup="menu"
                aria-expanded={Boolean(groupMenuAnchorEl)}
                onClick={handleOpenGroupMenu}
              >
                <Typography variant="subtitle2" component="span">
                  {t('team.tasks.groupBy')}
                </Typography>
                <span className="team-tasks-view__toolbar-button-separator" aria-hidden="true">
                  ·
                </span>
                <Typography variant="subtitle2" component="span">
                  {selectedGroupOption
                    ? getTeamTasksGroupLabel(t, selectedGroupOption.value)
                    : t('team.tasks.groups.due')}
                </Typography>
                <ExpandMoreRounded className="team-tasks-view__group-select-icon" />
              </button>
              <Menu
                anchorEl={groupMenuAnchorEl}
                open={Boolean(groupMenuAnchorEl)}
                onClose={handleCloseGroupMenu}
                className="team-tasks-view__group-menu"
                PaperProps={{
                  style: groupMenuAnchorEl
                    ? { width: groupMenuAnchorEl.getBoundingClientRect().width }
                    : undefined,
                }}
              >
                {availableGroupOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    className={clsx(
                      'team-tasks-view__group-menu-item',
                      groupBy === option.value && 'team-tasks-view__group-menu-item--selected'
                    )}
                    onClick={() => handleGroupByChange(option.value)}
                  >
                    <span className="team-tasks-view__group-menu-option">
                      <span className="team-tasks-view__shortcut-chip">
                        {GROUP_SHORTCUTS[option.value]}
                      </span>
                      <Typography variant="body2" component="span">
                        {getTeamTasksGroupLabel(t, option.value)}
                      </Typography>
                    </span>
                    {groupBy === option.value && (
                      <CheckRounded className="team-tasks-view__menu-check" />
                    )}
                  </MenuItem>
                ))}
              </Menu>

              <div
                className="team-tasks-view__layout-buttons"
                aria-label={t('team.tasks.layoutAria')}
              >
                {LAYOUT_OPTIONS.map((option) => {
                  const LayoutIcon = option.icon;
                  const layoutLabel = getTeamTasksLayoutLabel(t, option.value);

                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-label={t('team.tasks.switchLayoutAria', { layout: layoutLabel })}
                      title={t('team.tasks.layoutTitle', { layout: layoutLabel })}
                      className={clsx(
                        'team-tasks-view__layout-button',
                        layout === option.value && 'team-tasks-view__layout-button--active'
                      )}
                      onClick={() => setStoredLayout(option.value)}
                    >
                      <LayoutIcon className="team-tasks-view__layout-button-icon" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </header>

        {renderBody()}
      </main>
      <TaskDetailModal
        task={visibleTask!}
        visible={!!visibleTask}
        isReadOnly={isTaskModalReadOnly}
        readOnlyBadgeText={t('team.tasks.readOnlyBadge')}
        boardNameOverride={taskModalBoardName}
        onClose={() => {
          setVisibleTask(null);
          setIsTaskModalReadOnly(false);
          setTaskModalBoardName('');
        }}
      />
      <TaskDetailModal
        task={teamTaskModalTask || undefined}
        visible={!!teamTaskModalTask}
        isCreateMode={isCreatingTeamTask}
        boardNameOverride={
          activeBoards.find((board) => board.todoTaskId === teamTaskModalTask?.boardId)?.name || ''
        }
        teamTaskOptions={{
          boards: activeBoards,
          members,
          currentUserId,
          assigneeUserId: teamTaskModalAssigneeUserId,
          isSaving: isSavingTeamTask || isUpdatingTeamTaskAssignment,
          isDeleting: isDeletingTeamTask,
          onSaveTask: handleSaveTeamTask,
          onCompleteTask: async ({ taskId, boardId, completed }) => {
            await updateTeamTaskCompleteStatus({ teamId, boardId, taskId, completed });
          },
          onDeleteTask: async ({ taskId, boardId }) => {
            await deleteTeamTask({ teamId, boardId, taskId });
          },
          onAssignTask: async ({ taskId, boardId, assigneeUserId }) => {
            await updateTeamTaskAssignment({
              teamId,
              boardId,
              taskId,
              teamTaskAssignmentReq: { assigneeUserId },
            });
          },
          onOpenCreateBoard: canManageTeamBoards
            ? () => setIsCreateBoardDialogOpen(true)
            : undefined,
        }}
        onClose={handleCloseTeamTaskModal}
      />
      <AddProjectDialog
        isAdding={isCreatingTeamBoard}
        visible={isCreateBoardDialogOpen}
        existingProjectNames={activeBoards.map((board) => board.name)}
        onAdd={handleCreateTeamBoard}
        onClose={() => setIsCreateBoardDialogOpen(false)}
      />
    </>
  );
};

export default TeamTasksView;
